import {
  formatDisplayAmount,
  formatDisplayUnits,
} from "../helpers/amountDisplay";
import { AMOUNT_QUOTE_DEBOUNCE_MS } from "../../utils/amountQuote";
import { WalletAmountInput } from "./WalletAmountInput";
import React, { useEffect, useRef, useState } from "react";
import { ethers } from "ethers";
import { solanaWalletBytes, evmWalletAddress } from "../helpers/bridgeAddress";
import { styles } from "../../styles";
import type { TokenInfo } from "../../config";
import type { BaseStoredNote, BaseStatusMessage } from "../types";
import { requestPrivateFees } from "../../utils/relayer-client";
import {
  privatePools,
  privateSpendSource,
} from "../../joinsplit-v2/privatePools";
import {
  executePrivateFeeBridge,
  bridgeProviderName,
  type PrivateBridgeQuote,
  type PrivateBridgeRoute,
} from "../../joinsplit-v2/privateBridge";
import {
  decodePrivateBridge,
  bridgeDeploymentFor,
  isQualifiedBridgeRoute,
  ccipReceiverAddress,
  type PrivateBridgeProvider,
} from "../../joinsplit-v2/privateBridgeConfig";
import type { FlatJoinSplitV2OperationResult } from "../../joinsplit-v2/operationTypes";
import { PrivateFeeSummary } from "./PrivateFeeSummary";
import { friendlyError } from "../utils/errors";
import { getBaseReadProvider } from "../../utils/base-read-provider";

export interface BridgeSettledMeta {
  amount: string;
  symbol: string;
  destinationLabel: string;
  provider: string;
  providerName: string;
  trackingUrl: string;
}
const CCIP_SUBMITTED = new ethers.Interface([
  "event BridgeSubmitted(bytes32 indexed routeHash,address indexed sourcePool,address indexed escrow,address token,uint256 amount,uint64 destinationChainSelector,bytes receiver,bytes32 messageId,uint256 fee)",
]);
/** The tracker for a submitted bridge: the CCIP explorer for its message id
 * when the receipt is readable, Axelarscan for Axelar, else the source tx. */
async function trackingLinkFor(
  provider: string,
  txHash: string,
): Promise<string> {
  if (provider !== "ccip") return fallbackTracking(provider, txHash);
  try {
    const coordinator = bridgeDeploymentFor("ccip")?.coordinator?.toLowerCase();
    const receipt = await getBaseReadProvider().getTransactionReceipt(txHash);
    for (const log of receipt?.logs ?? []) {
      if (log.address.toLowerCase() !== coordinator) continue;
      try {
        const parsed = CCIP_SUBMITTED.parseLog({
          topics: [...log.topics],
          data: log.data,
        });
        const messageId = String(parsed?.args?.messageId || "");
        if (/^0x[0-9a-fA-F]{64}$/.test(messageId) && BigInt(messageId) !== 0n)
          return `https://ccip.chain.link/msg/${messageId.toLowerCase()}`;
      } catch {
        /* not the submitted event */
      }
    }
  } catch {
    /* receipt not readable yet; the transfers list keeps polling */
  }
  return fallbackTracking(provider, txHash);
}

interface Props {
  active: boolean;
  connected: boolean;
  busy: boolean;
  account: string | null;
  signer: ethers.Signer | null;
  spendingKeys: any;
  tokens: TokenInfo[];
  notes: BaseStoredNote[];
  onBusy: (busy: boolean) => void;
  onStatus: (s: BaseStatusMessage) => void;
  beforeSubmit: () => Promise<boolean>;
  onSettled: (
    r: FlatJoinSplitV2OperationResult,
    meta?: BridgeSettledMeta,
  ) => void;
}
interface RecordView {
  id: string;
  pool: string;
  provider: PrivateBridgeProvider;
  hash?: string;
  amount: string;
  destination: string;
  destinationLabel: string;
  deadline: number;
  status?: string;
  trackingUrl?: string;
}
const errorStyle = { color: "#ff5263", fontSize: 13, margin: "6px 0 12px" };
/** Secondary full-width action, the translucent style the wallet uses for non-primary controls. */
const secondaryStyle: React.CSSProperties = {
  ...styles.button,
  background: "rgba(240,244,255,0.08)",
  color: "#e8eeff",
  border: "1px solid rgba(255,255,255,0.15)",
};
const balanceStyle: React.CSSProperties = {
  fontSize: 12,
  color: "#b0c4de",
  margin: "4px 0 12px",
};
const PROVIDERS: { id: PrivateBridgeProvider | "debridge"; label: string }[] = [
  { id: "axelar", label: "Axelar" },
  { id: "ccip", label: "Chainlink CCIP" },
  { id: "debridge", label: "deBridge" },
];
const destinationName = (provider: string) =>
  provider === "ccip" ? "Ethereum Sepolia" : "Solana Devnet";
const fallbackTracking = (provider: string, hash: string) =>
  provider === "ccip"
    ? `https://sepolia.basescan.org/tx/${hash}`
    : `https://testnet.axelarscan.io/gmp/${hash}`;
/** Routes of providers this build was not configured with are ignored; a
 * configured provider must be served from exactly its pinned coordinator. */
function configuredRoutes(r: {
  coordinator?: string;
  coordinators?: Record<string, string>;
  routes: PrivateBridgeRoute[];
}): PrivateBridgeRoute[] {
  return r.routes.filter((x) => {
    const configured = bridgeDeploymentFor(x.provider)?.coordinator;
    if (!configured) return false;
    const served =
      r.coordinators?.[x.provider] ??
      (x.provider === "axelar" ? r.coordinator : undefined);
    if (served !== configured) throw Error("Bridge configuration changed");
    return true;
  });
}

export function PrivateBridgePanel(props: Props) {
  const [routes, setRoutes] = useState<PrivateBridgeRoute[]>([]),
    [provider, setProvider] = useState<string>("axelar");
  const [routeId, setRouteId] = useState(""),
    [amount, setAmount] = useState(""),
    [destination, setDestination] = useState("");
  const [quote, setQuote] = useState<{
      key: string;
      value: PrivateBridgeQuote;
    } | null>(null),
    [quoteBusy, setQuoteBusy] = useState(false);
  const [error, setError] = useState(""),
    [refresh, setRefresh] = useState(0),
    [running, setRunning] = useState(false),
    [elapsed, setElapsed] = useState(0);
  const [records, setRecords] = useState<RecordView[]>([]),
    [recordLimit, setRecordLimit] = useState(10),
    [recordError, setRecordError] = useState("");
  const scope = useRef(props.account);
  scope.current = props.account;
  const submitting = useRef(false);
  const completed = useRef(new Map<string, RecordView>());
  const [historyMore, setHistoryMore] = useState(false);
  useEffect(() => {
    setAmount("");
    setDestination("");
    setQuote(null);
    setRecords([]);
    setError("");
    setRoutes([]);
    setRouteId("");
    completed.current.clear();
  }, [props.account]);
  useEffect(() => {
    if (!props.active || !props.connected) return;
    const stop = new AbortController();
    requestPrivateFees<{
      routes: PrivateBridgeRoute[];
      chainId: number;
      coordinator?: string;
      coordinators?: Record<string, string>;
    }>("bridge-routes", {}, { signal: stop.signal })
      .then((r) => {
        if (
          r.chainId !== 84532 ||
          !Array.isArray(r.routes) ||
          !r.routes.every(isQualifiedBridgeRoute)
        )
          throw Error("Bridge configuration changed");
        const usable = configuredRoutes(r);
        if (!stop.signal.aborted) {
          setRoutes(usable);
          setProvider((previous) =>
            usable.some((x) => x.provider === previous)
              ? previous
              : usable[0]?.provider || previous,
          );
        }
      })
      .catch((e) => {
        if (!stop.signal.aborted) setError(friendlyError(e.message));
      });
    return () => stop.abort();
  }, [props.active, props.connected, props.account]);
  const providerRoutes = routes.filter((r) => r.provider === provider);
  useEffect(() => {
    setRouteId((previous) =>
      providerRoutes.some((x) => x.id === previous)
        ? previous
        : providerRoutes[0]?.id || "",
    );
    setDestination("");
  }, [provider, routes]);
  const route = providerRoutes.find((r) => r.id === routeId),
    token = props.tokens.find(
      (t) => t.address.toLowerCase() === route?.token.toLowerCase(),
    );
  let parsed = 0n;
  try {
    parsed = ethers.parseUnits(amount || "0", token?.decimals ?? 6);
  } catch {
    /* inline validation */
  }
  const source = privateSpendSource(
    props.notes,
    route?.token || ethers.ZeroAddress,
    parsed,
  );
  let destinationHex = "";
  try {
    destinationHex =
      provider === "ccip"
        ? evmWalletAddress(destination.trim()).toLowerCase()
        : solanaWalletBytes(destination.trim());
  } catch {
    /* inline validation */
  }
  const amountError =
    parsed <= 0n
      ? "Enter a valid amount greater than zero."
      : parsed > source.amount
        ? "Insufficient private balance."
        : "";
  const key = JSON.stringify([
    props.account,
    provider,
    routeId,
    source.pool,
    amount,
    destinationHex,
  ]);
  const selected = quote?.key === key ? quote.value : null;
  const valid =
    props.connected &&
    !props.busy &&
    (provider === "axelar" || provider === "ccip") &&
    !!route &&
    route.provider === provider &&
    !!token &&
    !amountError &&
    !!destinationHex;
  const inputCount = Math.min(
    32,
    props.notes.filter(
      (n) =>
        !n.spent &&
        n.note.poolAddress?.toLowerCase() === source.pool.toLowerCase() &&
        n.note.tokenData?.tokenAddress?.toLowerCase() ===
          route?.token.toLowerCase(),
    ).length,
  );
  useEffect(() => {
    setQuote(null);
    setError("");
    setQuoteBusy(false);
    if (!props.active || !valid || inputCount < 1) return;
    const stop = new AbortController();
    const timer = setTimeout(() => {
      setQuoteBusy(true);
      requestPrivateFees<PrivateBridgeQuote>(
        "bridge-quote",
        {
          provider,
          routeId,
          token: route!.token,
          amount: parsed.toString(),
          destinationAddress: destinationHex,
          inputCount,
        },
        { pool: source.pool, signal: stop.signal },
      )
        .then((q) => {
          if (!stop.signal.aborted) setQuote({ key, value: q });
        })
        .catch((e) => {
          if (!stop.signal.aborted) setError(friendlyError(e.message));
        })
        .finally(() => {
          if (!stop.signal.aborted) setQuoteBusy(false);
        });
    }, AMOUNT_QUOTE_DEBOUNCE_MS);
    return () => {
      clearTimeout(timer);
      stop.abort();
    };
    // Primitive request key prevents render-driven quote loops.
  }, [key, valid, inputCount, props.active, refresh]);
  useEffect(() => {
    if (!running) return;
    const started = Date.now();
    setElapsed(0);
    const timer = setInterval(
      () => setElapsed(Math.floor((Date.now() - started) / 1000)),
      1000,
    );
    return () => clearInterval(timer);
  }, [running]);
  useEffect(() => {
    if (!props.active || !props.connected || running) return;
    const stop = new AbortController();
    let timer: ReturnType<typeof setTimeout>;
    const load = async () => {
      if (document.visibilityState === "hidden") {
        timer = setTimeout(load, 60000);
        return;
      }
      try {
        const pages = await Promise.all(
          privatePools().map(async (pool) => {
            const records: any[] = [];
            let before = "",
              more = false;
            do {
              const page = await requestPrivateFees<{
                records: any[];
                nextCursor: string | null;
              }>(
                "bridge-records",
                { newest: true, before },
                { pool, signal: stop.signal },
              );
              records.push(...page.records);
              more = !!page.nextCursor;
              if (page.nextCursor === before)
                throw Error("Invalid history cursor");
              before = page.nextCursor || "";
            } while (before && records.length < recordLimit);
            return { pool, page: { records }, more };
          }),
        );
        const next: RecordView[] = pages
          .flatMap(({ pool, page }) =>
            (page.records || []).flatMap((r) => {
              const decoded = decodePrivateBridge(r.transaction?.data || "");
              if (
                !decoded ||
                decoded.route.refundRecipient.toLowerCase() !==
                  props.account?.toLowerCase()
              )
                return [];
              const destination =
                decoded.provider === "ccip"
                  ? ccipReceiverAddress(decoded.route.receiver)
                  : ethers.encodeBase58(
                      ethers.getBytes(decoded.route.destinationAddress),
                    );
              return [
                {
                  id: r.id,
                  pool,
                  provider: decoded.provider,
                  hash: r.transactionHash,
                  amount: ethers.formatUnits(decoded.route.amount, 6),
                  destination,
                  destinationLabel: destinationName(decoded.provider),
                  deadline: Number(decoded.route.deadline),
                },
              ];
            }),
          )
          .sort((a, b) => b.deadline - a.deadline);
        const visible = next.slice(0, recordLimit);
        const updated = await Promise.all(
          visible.map(async (r) => {
            const cached = completed.current.get(r.id);
            if (cached) return cached;
            try {
              const result = {
                ...r,
                ...(await requestPrivateFees<{
                  status: string;
                  trackingUrl?: string;
                }>(
                  "bridge-status",
                  { id: r.id, ...(r.hash ? { hash: r.hash } : {}) },
                  { pool: r.pool, signal: stop.signal },
                )),
              };
              if (
                !stop.signal.aborted &&
                ["delivered", "recovered", "expired"].includes(result.status)
              )
                completed.current.set(r.id, result);
              return result;
            } catch {
              return {
                ...r,
                status: "awaiting_delivery",
                trackingUrl: r.hash
                  ? fallbackTracking(r.provider, r.hash)
                  : undefined,
              };
            }
          }),
        );
        if (!stop.signal.aborted) {
          setRecords([...updated, ...next.slice(recordLimit)]);
          setHistoryMore(pages.some((p) => p.more));
          setRecordError("");
        }
      } catch {
        if (!stop.signal.aborted)
          setRecordError(
            "Transfer status is unavailable. Your saved transactions are retained.",
          );
      }
      if (!stop.signal.aborted) timer = setTimeout(load, 60000);
    };
    void load();
    return () => {
      stop.abort();
      clearTimeout(timer);
    };
  }, [props.active, props.connected, props.account, running, recordLimit]);
  const recover = async (record: RecordView) => {
    if (submitting.current) return;
    submitting.current = true;
    const account = props.account;
    try {
      if (!(await props.beforeSubmit())) return;
      setRunning(true);
      props.onBusy(true);
      setError("");
      await requestPrivateFees(
        "bridge-resume",
        { id: record.id },
        { pool: record.pool },
      );
      if (scope.current === account)
        props.onStatus({
          type: "info",
          message:
            "Recovery submitted. Check transfer status for confirmation.",
        });
    } catch (e: any) {
      if (scope.current === account) setError(friendlyError(e.message));
    } finally {
      submitting.current = false;
      setRunning(false);
      props.onBusy(false);
    }
  };
  const submit = async () => {
    if (
      submitting.current ||
      !valid ||
      !selected ||
      !props.signer ||
      !props.spendingKeys
    )
      return;
    if (Date.now() - selected.quotedAt > 120000) {
      setRefresh((n) => n + 1);
      setError("Refreshing the bridge quote. Review it before continuing.");
      return;
    }
    submitting.current = true;
    const account = props.account;
    try {
      if (!(await props.beforeSubmit())) return;
      setRunning(true);
      props.onBusy(true);
      setError("");
      props.onStatus({ type: "info", message: "Preparing transaction…" });
      const r = await executePrivateFeeBridge({
        signer: props.signer,
        spendingKeys: props.spendingKeys,
        notes: props.notes,
        tokenAddress: token!.address,
        tokenDecimals: token!.decimals,
        amount: parsed,
        recipient: ethers.ZeroAddress,
        relay: true,
        bridgeQuote: selected,
        onLog: () => {},
      });
      if (scope.current !== account) return;
      if (!r.success) throw Error(r.error || "Bridge could not be submitted");
      const trackingUrl = r.txHash
        ? await trackingLinkFor(provider, r.txHash)
        : "";
      const meta: BridgeSettledMeta = {
        amount: ethers.formatUnits(parsed, token!.decimals),
        symbol: token!.symbol,
        destinationLabel: destinationName(provider),
        provider,
        providerName: bridgeProviderName(provider),
        trackingUrl,
      };
      props.onSettled(r, meta);
      setAmount("");
      setQuote(null);
      props.onStatus({
        type: "success",
        transactionComplete: true,
        message: `Bridge submitted. ${bridgeProviderName(provider)} is delivering ${meta.amount} ${meta.symbol} to ${meta.destinationLabel}; testnet delivery usually takes 20 to 40 minutes.`,
        ...(trackingUrl
          ? { link: { href: trackingUrl, label: "Track transfer" } }
          : {}),
      });
    } catch (e: any) {
      if (scope.current === account) {
        setError(friendlyError(e.message));
        props.onStatus({ type: "error", message: friendlyError(e.message) });
      }
    } finally {
      submitting.current = false;
      setRunning(false);
      props.onBusy(false);
    }
  };
  const ccip = provider === "ccip";
  const ready = valid && !!selected && !quoteBusy && !running;
  const canMax = !running && source.amount > 0n && !!token;
  const showAmountError = amount.trim() !== "" && !!amountError;
  return (
    <section aria-label="Bridge tokens">
      <p style={styles.description}>
        Bridge from your private balance to a public {destinationName(provider)}{" "}
        wallet.
      </p>
      <label style={styles.label} htmlFor="bridge-provider">
        Provider
      </label>
      <select
        id="bridge-provider"
        style={styles.select}
        value={provider}
        disabled={running}
        onChange={(e) => setProvider(e.target.value)}
      >
        {PROVIDERS.map((p) => (
          <option key={p.id} value={p.id}>
            {p.label}
          </option>
        ))}
      </select>
      {provider === "debridge" ? (
        <p role="status" style={styles.description}>
          deBridge does not support Base Sepolia. It remains an option for
          supported mainnet deployments.
        </p>
      ) : (
        <>
          <label style={styles.label} htmlFor="bridge-route">
            Destination asset
          </label>
          <select
            id="bridge-route"
            style={styles.select}
            value={routeId}
            disabled={running}
            onChange={(e) => setRouteId(e.target.value)}
          >
            {!routes.length && <option value="">Loading routes…</option>}
            {!!routes.length && !providerRoutes.length && (
              <option value="">
                {bridgeProviderName(provider)} has no routes on Base Sepolia
              </option>
            )}
            {providerRoutes.map((r) => (
              <option key={r.id} value={r.id}>
                {r.assetLabel} · {r.destinationLabel}
              </option>
            ))}
          </select>
          {route && !ccip && (
            <p style={styles.description}>
              Mint:{" "}
              <a
                href={`https://explorer.solana.com/address/${route.destinationMint}?cluster=devnet`}
                target="_blank"
                rel="noreferrer"
              >
                {route.destinationMint}
              </a>
              <br />
              This test token represents Base Sepolia USDC; it is not
              Circle-issued Solana USDC.
            </p>
          )}
          {route && ccip && (
            <p style={styles.description}>
              Delivered as Circle-issued USDC on Ethereum Sepolia through
              Chainlink CCIP:{" "}
              <a
                href={`https://sepolia.etherscan.io/token/${route.destinationToken}`}
                target="_blank"
                rel="noreferrer"
              >
                {route.destinationToken}
              </a>
            </p>
          )}
          <label style={styles.label} htmlFor="bridge-recipient">
            {ccip ? "Ethereum wallet address" : "Solana wallet address"}
          </label>
          <input
            id="bridge-recipient"
            style={styles.input}
            value={destination}
            disabled={running}
            onChange={(e) => setDestination(e.target.value)}
            autoComplete="off"
            aria-invalid={!!destination && !destinationHex}
          />
          {destination && !destinationHex && (
            <p role="alert" style={errorStyle}>
              {ccip
                ? "Enter a valid Ethereum wallet address."
                : "Enter a valid Solana wallet address."}
            </p>
          )}
          <label style={styles.label} htmlFor="bridge-amount">
            Amount ({token?.symbol || "USDC"})
          </label>
          <WalletAmountInput
            id="bridge-amount"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="0.0"
            aria-describedby={
              showAmountError ? "bridge-amount-error" : undefined
            }
            aria-invalid={showAmountError}
            disabled={running}
            onMax={() =>
              token &&
              setAmount(ethers.formatUnits(source.amount, token.decimals))
            }
            maxDisabled={!canMax}
          />
          {showAmountError && (
            <p id="bridge-amount-error" role="status" style={errorStyle}>
              {amountError}
            </p>
          )}
          <div style={balanceStyle}>
            Available: {formatDisplayUnits(source.amount, token?.decimals ?? 6)}{" "}
            {token?.symbol || "USDC"}
          </div>
          {selected && (
            <PrivateFeeSummary
              action="bridge"
              amount={amount}
              policy={{
                ...selected.policy,
                symbol: token!.symbol,
                decimals: token!.decimals,
              }}
            />
          )}
          {selected && !ccip && (
            <p style={styles.description}>
              Network fee includes Base submission and Axelar delivery. Your
              connected wallet is the public recovery and gas-refund address on
              Base. Tokens arrive publicly on Solana. Testnet delivery can take
              20 minutes or more while network confirmations complete. You can
              close this panel and return to track your transfer.
            </p>
          )}
          {selected && ccip && (
            <p style={styles.description}>
              Network fee includes Base submission and the Chainlink CCIP
              delivery fee. Your connected wallet is the public recovery address
              on Base. Tokens arrive publicly on Ethereum Sepolia. Testnet
              delivery can take 20 minutes or more while Circle attestation and
              CCIP confirmations complete. You can close this panel and return
              to track your transfer.
            </p>
          )}
          {quoteBusy && <p role="status">Checking bridge fees…</p>}
          {running && (
            <p role="status">
              {elapsed < 20
                ? "Preparing transaction…"
                : "Processing your transaction…"}{" "}
              {Math.floor(elapsed / 60)}:{String(elapsed % 60).padStart(2, "0")}
            </p>
          )}
          <button
            type="button"
            style={{ ...secondaryStyle, marginBottom: "12px" }}
            disabled={running || quoteBusy || !valid}
            onClick={() => setRefresh((n) => n + 1)}
          >
            Refresh quote
          </button>
          <button
            className="wallet-cta"
            type="button"
            style={{
              ...styles.button,
              ...styles.primaryButton,
              opacity: ready ? 1 : 0.5,
              cursor: ready ? "pointer" : "not-allowed",
            }}
            disabled={!ready}
            onClick={() => void submit()}
          >
            {running ? "Processing…" : "Bridge"}
          </button>
        </>
      )}
      {error && (
        <p role="alert" style={errorStyle}>
          {error}
        </p>
      )}
      {!!records.length && (
        <div aria-label="Bridge activity">
          <h3>Transfers</h3>
          {records.slice(0, recordLimit).map((r) => (
            <div
              key={r.id}
              style={{
                borderTop: "1px solid #26314b",
                padding: "12px 0",
                overflowWrap: "anywhere",
              }}
            >
              <strong>
                {formatDisplayAmount(r.amount)} USDC → {r.destinationLabel}
              </strong>
              <p>
                {r.status === "delivered"
                  ? "Delivered"
                  : r.status === "recovered"
                    ? "Returned to your connected wallet"
                    : r.status === "recoverable"
                      ? "Ready to return to your connected wallet"
                      : r.status === "expired"
                        ? "Expired before bridge submission"
                        : r.status === "delivery_delayed"
                          ? "Delivery delayed — track transfer for details"
                          : r.hash
                            ? "Awaiting destination delivery"
                            : "Prepared — source confirmation pending"}
              </p>
              <small>{r.destination}</small>
              <br />
              {r.trackingUrl && (
                <a href={r.trackingUrl} target="_blank" rel="noreferrer">
                  {r.provider === "ccip"
                    ? "Track transfer"
                    : "Track on Axelarscan"}
                </a>
              )}
              {r.status === "recoverable" && (
                <button
                  type="button"
                  style={{ ...secondaryStyle, marginTop: 10 }}
                  disabled={running || props.busy}
                  onClick={() => void recover(r)}
                >
                  Return to connected wallet
                </button>
              )}
            </div>
          ))}
          {(records.length > recordLimit || historyMore) && (
            <button
              type="button"
              className="wallet-text-button"
              onClick={() => setRecordLimit((n) => n + 10)}
            >
              Show more
            </button>
          )}
        </div>
      )}
      {recordError && <p role="status">{recordError}</p>}
    </section>
  );
}
